import { useCallback } from 'react';

import type { SidekickActingOrganizationScope } from '../api/actingOrganization';
import { useConversationServiceCreateScopedConversationMutation } from '../api/overrides';
import type { EnsuredSidekickConversation } from '../contexts/SidekickApiContext';

const MAX_CREATE_ATTEMPTS = 2;
const CREATE_RETRY_BACKOFF_MS = 500;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const getCreateFailureLabel = (error: unknown): string => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return error instanceof Error ? error.name : 'unknown';
  }
  const { status } = error as { status?: unknown };
  return typeof status === 'string' || typeof status === 'number'
    ? `status ${status}`
    : 'unknown';
};

const isNotFoundError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  error.status === 404;

const isNonRetryableCreateError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  error.status === 'CUSTOM_ERROR';

/** Options for creating a conversation. */
export type CreateConversationOptions = {
  /**
   * Backend id of the question an edit replaces. The new conversation
   * then keeps the exchanges before it.
   */
  editedMessageId?: string;
};

/**
 * Creates a new backend conversation with one retry for transient failures.
 * @returns A stable callback that always creates a fresh conversation
 */
export const useCreateConversation = (): ((
  organizationScope: SidekickActingOrganizationScope,
  options?: CreateConversationOptions
) => Promise<EnsuredSidekickConversation>) => {
  const [createConversationMutation] =
    useConversationServiceCreateScopedConversationMutation();

  return useCallback(
    async (
      organizationScope: SidekickActingOrganizationScope,
      options?: CreateConversationOptions
    ) => {
      let editedMessageId = options?.editedMessageId;
      for (let attempt = 1; attempt <= MAX_CREATE_ATTEMPTS; attempt += 1) {
        try {
          const response = await createConversationMutation({
            createConversationRequest:
              editedMessageId !== undefined ? { editedMessageId } : {},
            organizationScope,
          }).unwrap();
          const conversationId = response.conversationId;
          if (conversationId === undefined || conversationId === '') {
            throw new Error('createConversation returned no conversationId');
          }

          return {
            conversationId,
            createdAt: response.createdAt ?? new Date().toISOString(),
          };
        } catch (error) {
          // The backend doesn't know the edited question, for example when it
          // never stored it. Branch without it so the edit still goes through,
          // just without the earlier exchanges.
          if (editedMessageId !== undefined && isNotFoundError(error)) {
            editedMessageId = undefined;
            attempt -= 1;
            continue;
          }
          if (
            isNonRetryableCreateError(error) ||
            attempt >= MAX_CREATE_ATTEMPTS
          ) {
            throw error;
          }
          if (import.meta.env.DEV) {
            console.warn(
              '[useCreateConversation] createConversation failed',
              getCreateFailureLabel(error)
            );
          }
          await sleep(CREATE_RETRY_BACKOFF_MS);
        }
      }

      throw new Error('Sidekick conversation creation failed');
    },
    [createConversationMutation]
  );
};
